import { create } from 'zustand';
import { NodeType } from '@/types/tree';

/**
 * Represents a reference to a node in the tree structure
 * @interface NodeReference
 * @property {NodeType} type - The type of the node
 * @property {string} id - The unique identifier of the node
 */
interface NodeReference {
  type: NodeType;
  id: string;
}

/**
 * Manages UI state for node operations in the application
 * @interface UIState
 * @property {NodeReference | null} editingNode - Currently selected node for editing
 * @property {NodeReference | null} viewingNode - Currently selected node for viewing
 * @property {NodeReference | null} sharingNode - Currently selected node for sharing
 * @property {Function} setEditingNode - Updates the node being edited
 * @property {Function} setViewingNode - Updates the node being viewed
 * @property {Function} setSharingNode - Updates the node being shared
 */
interface UIState {
  editingNode: NodeReference | null;
  viewingNode: NodeReference | null;
  sharingNode: NodeReference | null;

  setEditingNode: (node: NodeReference | null) => void;
  setViewingNode: (node: NodeReference | null) => void;
  setSharingNode: (node: NodeReference | null) => void;
}

/**
 * Zustand store for managing UI state related to node operations
 * Provides state and actions for tracking which node is being edited, viewed, or shared
 */
export const useUIStateStore = create<UIState>()((set) => ({
  editingNode: null,
  viewingNode: null,
  sharingNode: null,

  setEditingNode: (node) => set({ editingNode: node }),
  setViewingNode: (node) => set({ viewingNode: node }),
  setSharingNode: (node) => set({ sharingNode: node }),
}));
